import React, { useState, useEffect, useCallback } from "react";
import { Plus, AlertCircle, Search, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { purchaseAPI, batchAPI, tagAPI } from "../../services";
import BatchForm from "./BatchForm";
import StockForm from "./StockForm";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import "../../style/productDetail.css";
import "../../style/batches.css";
import "../../style/add-by-tag-modal.css";

import EditIcon from "../../assets/icons/EditIcon.js";
import PrintIcon from "../../assets/icons/PrintIcon.js";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon.js";

import BackIcon from "../../assets/icons/BackIcon";
import PushIcon from "../../assets/icons/PushIcon";
import NoStock from "../../assets/no-stock.svg";
import PushModalIcon from "../../assets/push-modal-icon.svg";
import { useSelector } from "react-redux";

const BatchDetailView = ({
  batch,
  onBack,
  onEditBatch,
  onAddStock,
  onEditStock,
  onDeleteStock,
  products = [],
  suppliers = [],
  allBatches = [],
}) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [showStockForm, setShowStockForm] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [stockItems, setStockItems] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [batchDetail, setBatchDetail] = useState(null);
  const [loadingBatchDetail, setLoadingBatchDetail] = useState(false);
  const [displayedItems, setDisplayedItems] = useState(30);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [showPushConfirmation, setShowPushConfirmation] = useState(false);
  const [showAddByTagModal, setShowAddByTagModal] = useState(false);
  const [showPriceByTagModal, setShowPriceByTagModal] = useState(false);
  const [tagFormData, setTagFormData] = useState({
    selectedTag: "",
    quantity: "",
    buyingPrice: "",
    sellingPrice: "",
    applyToAll: false,
  });
  const [tagProducts, setTagProducts] = useState([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [availableTags, setAvailableTags] = useState([]);
  const [, setLoadingTags] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [, setLoadingTagProducts] = useState(false);
  const [submittingTag, setSubmittingTag] = useState(false);
  const [, setOriginalTagFormData] = useState({
    buyingPrice: "",
    sellingPrice: "",
    quantity: "",
  });
  const [productPrices, setProductPrices] = useState({});
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector(
    (state) => state.domain
  );
  const handlePushClick = () => {
    if (isGodownURL) {
      setShowPushConfirmation(true);
    }
  };
  const handleTagClick = () => {
    if (isGodownURL) {
      handlePriceByTagClick();
    }
  };

  const handlePushConfirm = async () => {
    try {
      await batchAPI.publishBatch(batch.id);
      toast.success("Batch published successfully!");
      setShowPushConfirmation(false);
      await fetchBatchDetail("");
    } catch (error) {
      console.error("Error publishing batch:", error);
      toast.error(error.response?.data?.message || "Failed to publish batch");
    }
  };

  const handlePushCancel = () => {
    setShowPushConfirmation(false);
  };

  const handleAddByTagClick = () => {
    setShowAddByTagModal(true);
    fetchTags();
  };

  const handlePriceByTagClick = () => {
    setShowPriceByTagModal(true);
    fetchTags("", true);
  };

  const handleAddByTagCancel = () => {
    setShowAddByTagModal(false);
    setTagFormData({
      selectedTag: "",
      quantity: "",
      buyingPrice: "",
      sellingPrice: "",
      applyToAll: false,
    });
    setTagProducts([]);
    setTagDropdownOpen(false);
    setTagSearchQuery("");
    setSelectedTagId(null);
    setProductPrices({});
  };

  const handleByTagCancel = () => {
    setShowPriceByTagModal(false);
    setTagFormData({
      selectedTag: "",
      quantity: "",
      buyingPrice: "",
      sellingPrice: "",
      applyToAll: false,
    });
    setOriginalTagFormData({
      buyingPrice: "",
      sellingPrice: "",
      quantity: "",
    });
    setTagProducts([]);
    setTagDropdownOpen(false);
    setTagSearchQuery("");
    setSelectedTagId(null);
    setProductPrices({});
  };

  const handleAddByTagSubmit = async () => {
    if (submittingTag) return;

    try {
      if (!selectedTagId) {
        toast.error("Please select a tag");
        return;
      }

      setSubmittingTag(true);
      const products = tagProducts.map(product => {
        const prices = productPrices[product.id] || {};
        return {
          product_id: product.id,
          quantity: parseInt(prices.quantity || tagFormData.quantity || 0),
          buying_price: parseFloat(prices.buyingPrice || tagFormData.buyingPrice || 0),
          selling_price: parseFloat(prices.sellingPrice || tagFormData.sellingPrice || 0),
          record_point: 10,
          unique_id: product.unique_id || ""
        };
      });

      const payload = {
        unique_identifier: selectedTagId.toString(),
        batch_id: batch.id,
        products: products
      };

      if (isGodownURL) {
        await batchAPI.createBatchPurchaseByTag(payload);
      } else {
        await purchaseAPI.createPurchaseByTag(payload);
      }
      toast.success("Tags added successfully!");
      handleAddByTagCancel();
      await fetchBatchDetail("");
    } catch (error) {
      console.error("Error adding tags:", error);
      toast.error(error.response?.data?.message || "Failed to add tags");
    } finally {
      setSubmittingTag(false);
    }
  };

  const handleTagFormChange = (field, value) => {
    if ((field === 'quantity' || field === 'buyingPrice' || field === 'sellingPrice') && value < 0) {
      return;
    }
    setTagFormData((prev) => ({ ...prev, [field]: value }));

    // Update all product prices when Apply to All is checked
    if (field === 'applyToAll' && value === true) {
      const updatedPrices = {};
      tagProducts.forEach(product => {
        updatedPrices[product.id] = {
          buyingPrice: tagFormData.buyingPrice,
          sellingPrice: tagFormData.sellingPrice,
          quantity: tagFormData.quantity
        };
      });
      setProductPrices(updatedPrices);
    } else if (tagFormData.applyToAll && (field === 'quantity' || field === 'buyingPrice' || field === 'sellingPrice')) {
      const updatedPrices = {};
      tagProducts.forEach(product => {
        updatedPrices[product.id] = {
          ...productPrices[product.id],
          [field]: value
        };
      });
      setProductPrices(updatedPrices);
    }
  };

  const handlePriceByTagSubmit = async () => {
    if (submittingTag) return;

    try {
      if (!selectedTagId) {
        toast.error("Please select a tag");
        return;
      }

      setSubmittingTag(true);

      // Build products array with individual product prices
      const products = tagProducts.map(product => {
        const prices = productPrices[product.id] || {};
        const productData = {
          product_id: product.product || product.product_id,
        };

        // Include updated fields
        if (prices.buyingPrice) {
          productData.buying_price = parseFloat(prices.buyingPrice);
        }

        if (prices.sellingPrice) {
          productData.selling_price = parseFloat(prices.sellingPrice);
        }

        if (prices.quantity) {
          productData.quantity = parseInt(prices.quantity);
          productData.available_qty = parseInt(prices.quantity);
        }

        // Always include these fields
        productData.record_point = 10;
        productData.unique_id = product.unique_id || "";

        return productData;
      });

      const payload = {
        unique_identifier: selectedTagId.toString(),
        batch_id: batch.id,
        products: products
      };

      console.log('Updating prices with payload:', payload);
      if (isGodownURL) {
        await batchAPI.updateBatchPurchaseByTag(payload);
      } else {
        await purchaseAPI.updatePurchaseByTag(payload);
      }
      toast.success("Prices updated successfully!");
      handleByTagCancel();
      await fetchBatchDetail("");
    } catch (error) {
      console.error("Error updating prices:", error);
      toast.error(error.response?.data?.message || "Failed to update prices");
      setSubmittingTag(false);
    }
  };

  const handleRemoveTagProduct = (index) => {
    const productId = tagProducts[index]?.id;
    setTagProducts((prev) => prev.filter((_, i) => i !== index));
    if (productId) {
      setProductPrices((prev) => {
        const newPrices = { ...prev };
        delete newPrices[productId];
        return newPrices;
      });
    }
  };

  const handleProductPriceChange = (productId, field, value) => {
    if (value < 0) return;
    setProductPrices((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
  };

  // Fetch tags from API
  const fetchTags = useCallback(async (search = "", isPriceByTag = false) => {
    setLoadingTags(true);
    try {
      const params = { search, page_size: 100 };
      if (isPriceByTag && batch?.id) {
        params.batch_id = batch.id;
      }
      const response = await tagAPI.getTagsList(params);
      setAvailableTags(response.results || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast.error("Failed to load tags");
    } finally {
      setLoadingTags(false);
    }
  }, [batch?.id]);

  // Debounce tag search
  useEffect(() => {
    if ((showAddByTagModal || showPriceByTagModal) && tagSearchQuery) {
      const timeoutId = setTimeout(() => {
        fetchTags(tagSearchQuery, showPriceByTagModal);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [tagSearchQuery, showPriceByTagModal, showAddByTagModal, fetchTags]);

  const filteredTags = availableTags;

  const handleTagSelect = async (tag) => {
    handleTagFormChange("selectedTag", tag.identifier);
    setSelectedTagId(tag.id);
    setTagDropdownOpen(false);
    setTagSearchQuery("");

    setLoadingTagProducts(true);
    try {
      if (showPriceByTagModal) {
        console.log('Fetching products for Price By Tag modal:', tag.id, batch.id);
        const response = isGodownURL
          ? await batchAPI.getBatchPurchaseByTag({
            unique_identifier: tag.id,
            batch_id: batch.id
          })
          : await purchaseAPI.getPurchaseByTag({
            unique_identifier: tag.id,
            batch_id: batch.id
          });
        console.log('Price By Tag response:', response);
        // API returns data in "data" field, not "results"
        const products = response.data || [];
        setTagProducts(products);

        // Pre-fill form with existing values from first product if available
        if (products.length > 0) {
          const firstProduct = products[0];
          const originalValues = {
            buyingPrice: firstProduct.buying_price?.toString() || "",
            sellingPrice: firstProduct.selling_price?.toString() || "",
            quantity: firstProduct.available_qty?.toString() || firstProduct.initial_quantity?.toString() || ""
          };

          // Store original values for comparison
          setOriginalTagFormData(originalValues);

          // Set form data with applyToAll checked by default
          setTagFormData(prev => ({
            ...prev,
            buyingPrice: "",
            sellingPrice: "",
            quantity: "",
            applyToAll: false
          }));

          // Initialize productPrices with existing values for each product
          const initialPrices = {};
          products.forEach(product => {
            initialPrices[product.id] = {
              buyingPrice: product.buying_price?.toString() || "",
              sellingPrice: product.selling_price?.toString() || "",
              quantity: (product.available_qty || product.initial_quantity)?.toString() || ""
            };
          });
          setProductPrices(initialPrices);
        }
      } else {
        const response = await tagAPI.getProductsByTag(tag.id);
        const products = response.results || [];
        setTagProducts(products);
        setProductPrices({});
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
      setTagProducts([]);
    } finally {
      setLoadingTagProducts(false);
    }
  };

  // Helper function to format field names for better display
  const formatFieldName = (fieldName) => {
    const fieldMap = {
      unique_id: "Unique ID",
      product_id: "Product",
      batch_id: "Batch",
      buying_price: "Buying Price",
      selling_price: "Selling Price",
      record_point: "Record Point",
      available_qty: "Available Quantity",
    };

    return (
      fieldMap[fieldName] ||
      fieldName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  const handleEditBatchSave = (batchData) => {
    onEditBatch(batchData);
    setShowEditForm(false);
  };

  // Convert API data to form format for editing
  const getEditableBatch = () => {
    if (!batch) return null;
    const editableBatch = {
      ...batch,
      invoiceNumber: batch.invoice_number,
      totalQuantity: batch.quantity,
      supplierId: batch.vendor,
      description: batch.description || "",
    };
    return editableBatch;
  };

  const handleAddStock = () => {
    setEditingStock(null);
    setShowStockForm(true);
  };

  const handleEditStock = (stock) => {
    setEditingStock(stock);
    setShowStockForm(true);
  };

  const handleStockFormSubmit = async (stockData) => {
    try {
      if (editingStock) {
        // Update existing purchase
        await handleEditStockAPI(editingStock.id, stockData);
        toast.success("Stock item updated successfully!");
      } else {
        // Create new purchase
        await handleAddStockAPI(batch.id, stockData);
        toast.success("Stock item added successfully!");
      }
      // Clear search and refresh batch details after operation
      setSearchQuery("");
      setDebouncedSearchQuery("");
      await fetchBatchDetail("");
      setShowStockForm(false);
      setEditingStock(null);
    } catch (error) {
      console.error("Error submitting stock form:", error);
      // Handle validation errors from backend
      if (error.response?.data) {
        const errorData = error.response.data;

        // Check for the specific error format: { status, message, errors }
        if (errorData.errors && typeof errorData.errors === "object") {
          let hasShownError = false;
          Object.keys(errorData.errors).forEach((field) => {
            const fieldErrors = errorData.errors[field];
            const formattedFieldName = formatFieldName(field);
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach((errorMsg) => {
                toast.error(`${formattedFieldName}: ${errorMsg}`);
                hasShownError = true;
              });
            } else if (typeof fieldErrors === "string") {
              toast.error(`${formattedFieldName}: ${fieldErrors}`);
              hasShownError = true;
            }
          });

          if (!hasShownError && errorData.message) {
            toast.error(errorData.message);
          }
        }
        // Check if it's a validation error with field-specific messages (alternative format)
        else if (
          typeof errorData === "object" &&
          !Array.isArray(errorData) &&
          !errorData.status
        ) {
          let hasShownError = false;
          Object.keys(errorData).forEach((field) => {
            const fieldErrors = errorData[field];
            const formattedFieldName = formatFieldName(field);
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach((errorMsg) => {
                toast.error(`${formattedFieldName}: ${errorMsg}`);
                hasShownError = true;
              });
            } else if (typeof fieldErrors === "string") {
              toast.error(`${formattedFieldName}: ${fieldErrors}`);
              hasShownError = true;
            }
          });

          if (!hasShownError) {
            toast.error(
              editingStock
                ? "Failed to update stock item"
                : "Failed to add stock item"
            );
          }
        } else if (typeof errorData === "string") {
          toast.error(errorData);
        } else if (errorData.detail) {
          toast.error(errorData.detail);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else {
          toast.error(
            editingStock
              ? "Failed to update stock item"
              : "Failed to add stock item"
          );
        }
      } else {
        toast.error(
          editingStock
            ? "Failed to update stock item"
            : "Failed to add stock item"
        );
      }
    }
  };

  // Fetch batch details from API
  const fetchBatchDetail = useCallback(
    async (search = "") => {
      if (!batch?.id) return;

      setLoadingBatchDetail(true);
      setSearchLoading(true);
      try {
        if (search) {
          // Use appropriate API based on domain
          const response = isGodownURL
            ? await batchAPI.getBatchPurchasesList({
              batch_id: batch.id,
              search: search,
              page_size: 1000,
            })
            : await purchaseAPI.getPurchasesList({
              batch_id: batch.id,
              search: search,
              page_size: 1000,
            });
          setStockItems(response.results || []);
        } else {
          // Use batch detail API for full data
          const response = await batchAPI.getBatchDetail(batch.id);
          if (response && response.data) {
            setBatchDetail(response.data);
            setStockItems(response.data.products_list || []);
          }
        }
      } catch (error) {
        console.error("Error fetching batch detail:", error);
        if (!search) {
          setBatchDetail(null);
        }
        setStockItems([]);
      } finally {
        setLoadingBatchDetail(false);
        setSearchLoading(false);
      }
    },
    [batch?.id, isGodownURL]
  );

  // Debounce search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Load batch details when batch changes or debounced search changes
  useEffect(() => {
    fetchBatchDetail(debouncedSearchQuery);
  }, [fetchBatchDetail, debouncedSearchQuery]);

  // Handle search input
  // const handleSearchChange = (e) => {
  //   setSearchQuery(e.target.value);
  // };

  // Infinite scroll handler
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollHeight - scrollTop <= clientHeight + 50 &&
        !loadingStocks &&
        hasMore
      ) {
        setLoadingStocks(true);
        setTimeout(() => {
          setDisplayedItems((prev) => {
            const newCount = prev + 30;
            setHasMore(newCount < stockItems.length);
            return newCount;
          });
          setLoadingStocks(false);
        }, 500);
      }
    },
    [loadingStocks, hasMore, stockItems.length]
  );

  // Reset displayed items when stock items change
  useEffect(() => {
    setDisplayedItems(30);
    setHasMore(stockItems.length > 30);
  }, [stockItems.length, stockItems]);

  // Add stock using purchase API
  const handleAddStockAPI = async (batchId, stockData) => {
    try {
      const uniqueIdValue = stockData.uniqueId;

      const purchasePayload = {
        product: stockData.productId,
        batch: batchId,
        quantity: stockData.quantity,
        selling_price: stockData.sellingPrice,
        buying_price: stockData.buyingPrice,
        record_point: 10,
        available_qty: stockData.quantity,
      };

      if (uniqueIdValue && uniqueIdValue.trim() !== "") {
        purchasePayload.unique_id = uniqueIdValue;
      }

      if (isGodownURL) {
        await batchAPI.createBatchPurchase(purchasePayload);
      } else {
        await purchaseAPI.createPurchase(purchasePayload);
      }
    } catch (error) {
      console.error("Error adding stock:", error);
      throw error;
    }
  };

  // Edit stock using purchase API
  const handleEditStockAPI = async (purchaseId, stockData) => {
    try {
      const updatePayload = {
        selling_price: stockData.sellingPrice,
        buying_price: stockData.buyingPrice,
      };

      if (stockData.uniqueId && stockData.uniqueId.trim() !== "") {
        updatePayload.unique_id = stockData.uniqueId.trim();
      }

      if (isGodownURL) {
        await batchAPI.updateBatchPurchase(purchaseId, updatePayload);
      } else {
        await purchaseAPI.updatePurchase(purchaseId, updatePayload);
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      throw error;
    }
  };

  // Delete stock using purchase API
  const handleDeleteStockAPI = async (purchaseId) => {
    try {
      if (isGodownURL) {
        await batchAPI.deleteBatchPurchase(purchaseId);
      } else {
        await purchaseAPI.deletePurchase(purchaseId);
      }

      toast.success("Stock item deleted successfully!");

      // Clear search and refresh batch details after deletion
      setSearchQuery("");
      setDebouncedSearchQuery("");
      await fetchBatchDetail("");
    } catch (error) {
      console.error("Error deleting stock:", error);

      // Handle error response
      if (error.response?.data) {
        const errorData = error.response.data;

        // Check for the specific error format: { status, message, errors }
        if (errorData.errors && typeof errorData.errors === "object") {
          let hasShownError = false;
          Object.keys(errorData.errors).forEach((field) => {
            const fieldErrors = errorData.errors[field];
            const formattedFieldName = formatFieldName(field);
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach((errorMsg) => {
                toast.error(`${formattedFieldName}: ${errorMsg}`);
                hasShownError = true;
              });
            } else if (typeof fieldErrors === "string") {
              toast.error(`${formattedFieldName}: ${fieldErrors}`);
              hasShownError = true;
            }
          });

          if (!hasShownError && errorData.message) {
            toast.error(errorData.message);
          }
        } else if (typeof errorData === "string") {
          toast.error(errorData);
        } else if (errorData.detail) {
          toast.error(errorData.detail);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else {
          toast.error("Failed to delete stock item");
        }
      } else {
        toast.error("Failed to delete stock item");
      }

      throw error;
    }
  };

  const getSupplierName = () => {
    // Use vendor_details from API response if available
    if (batchDetail?.vendor_details) {
      return batchDetail.vendor_details.name;
    }
    if (batch?.vendor_details) {
      return batch.vendor_details.name;
    }
    // Fallback to suppliers array lookup
    const vendorId = batchDetail?.vendor || batch?.vendor;
    const supplier = suppliers.find((s) => s.id === vendorId);
    return supplier ? supplier.name : "Unknown Supplier";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatPrice = (price) => {
    return parseFloat(price).toString();
  };

  const handlePrintBatch = () => {
    if (!stockItems || stockItems.length === 0) {
      toast.error("No items to print");
      return;
    }

    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.text(`Batch: ${displayBatch.batch_number}`, 14, 15);
    doc.setFontSize(10);
    doc.text(`Invoice: ${displayBatch.invoice_number || '-'}`, 14, 22);
    doc.text(`Date: ${formatDate(displayBatch.created_at)}`, 14, 28);

    // Prepare table data
    const tableData = stockItems.map((stock) => {
      const productName = stock.product_name || "Unknown Product";
      const details = [
        stock.product_detail?.ram,
        stock.product_detail?.storage,
        stock.product_detail?.variants?.map((v) => Object.values(v).join(", ")).join(", "),
        stock.product_detail?.suffix,
        stock.product_detail?.used_mobile !== undefined && stock.product_detail?.used_mobile !== null
          ? stock.product_detail.used_mobile === false ? "New" : "Used"
          : null,
      ].filter(Boolean).join(" | ");

      const fullProductName = details ? `${productName}\n${details}` : productName;

      return [
        fullProductName,
        stock.category_name || "-",
        stock.product_detail?.brand_name || "-",
        stock.initial_quantity || 0,
        stock.product_detail?.unique_identifier_identifier || "-",
        `€${formatPrice(stock.buying_price || 0)}`,
        `€${formatPrice(stock.selling_price || 0)}`
      ];
    });

    autoTable(doc, {
      startY: 35,
      head: [["Product", "Category", "Brand", "Qty", "Tag", "Buy Price", "Sell Price"]],
      body: tableData,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [12, 174, 0], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 25 },
        2: { cellWidth: 25 },
        3: { cellWidth: 15 },
        4: { cellWidth: 15 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 }
      }
    });

    doc.save(`Batch_${displayBatch.batch_number}.pdf`);
  };

  if (!batch) {
    return (
      <div className="batches-detail-page">
        <div className="batches-detail-container">
          <div className="back-navigation">
            <button className="back-button" onClick={onBack}>
              <img src={BackIcon} alt="Back" />
              Back to Batches
            </button>
          </div>
          <div className="no-data-container">
            <div className="no-data-icon">
              <AlertCircle size={48} />
            </div>
            <div className="no-data-text">Batch not found</div>
          </div>
        </div>
      </div>
    );
  }

  const stocks = stockItems || [];

  // Sort stocks by creation date (oldest first) to ensure consistent ordering
  const sortedStocks = [...stocks].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateA - dateB; // Ascending order (oldest first)
  });

  // Reverse the sorted array so newest items appear first at the top
  const reversedStocks = [...sortedStocks].reverse();
  const displayedStocks = reversedStocks.slice(0, displayedItems);

  const usedQuantity = stocks.reduce(
    (sum, stock) => sum + (stock.available_qty || stock.quantity || 0),
    0
  );

  // Calculate total amount from stock items or use batchDetail data
  const totalAmountFromStocks =
    batchDetail?.total_amount ||
    stocks.reduce((sum, stock) => sum + (stock.total_amount || 0), 0);

  // Use batchDetail data if available, otherwise fall back to batch data
  const displayBatch = batchDetail || batch;

  return (
    <div className="batches-detail-page">
      <div className="batches-detail-container">
        <div className="back-navigation">
          <button
            className={`${isPartsURL
              ? "back-button-parts"
              : isAccessoriesURL
                ? "back-button-acc"
                : isGodownURL
                  ? "back-button-godown"
                  : "back-button"
              }`}
            onClick={onBack}
          >
            <BackIcon
              fill={
                isPartsURL
                  ? "var(--primary-color-parts)"
                  : isAccessoriesURL
                    ? "var(--primary-color-acc)"
                    : isGodownURL
                      ? "var(--primary-color-godown)"
                      : "var(--primary-color-mobile)"
              }
            />
            Back to Batches
          </button>
        </div>

        <div className="batches-detail-content">
          <div className="page-header">
            <div className="page-header-content">
              <div className="page-title-section">
                <h1 className="page-title">Batch Details</h1>
                <p className="page-subtitle">
                  Complete information about batch{" "}
                  {displayBatch.invoice_number || displayBatch.id}
                </p>
              </div>
              <div className="page-actions">
                {
                  isGodownURL ? (
                    <>
                      {displayBatch.is_published ? (
                        <button
                          className={`${isPartsURL
                            ? "add-brand-btn-parts"
                            : isAccessoriesURL
                              ? "add-brand-btn-acc"
                              : isGodownURL
                                ? "add-brand-btn-godown"
                                : "add-brand-btn"
                            }`}
                          onClick={handlePrintBatch}
                        >
                          <PrintIcon fill={"#fff"} />
                          Print
                        </button>
                      ) : (
                        <>
                          <button
                            className={`btn ${isPartsURL
                              ? "btn-outline-primary-parts batch-push-btn-parts"
                              : isAccessoriesURL
                                ? "btn-outline-primary-acc batch-push-btn-acc"
                                : isGodownURL
                                  ? "batch-push-btn-godown"
                                  : "btn-outline-primary batch-push-btn"
                              }`}
                            onClick={handlePushClick}
                          >
                            <PushIcon
                              fill={
                                isPartsURL
                                  ? "var(--primary-color-parts)"
                                  : isAccessoriesURL
                                    ? "var(--primary-color-acc)"
                                    : isGodownURL
                                      ? "#0CAE00"
                                      : "var(--primary-color-mobile)"
                              }
                            />
                            Push
                          </button>
                          {isGodownURL && (
                            <button
                              className="add-by-tag-btn-godown"
                              onClick={handleAddByTagClick}
                            >
                              <Plus size={16} />
                              Add By Tag
                            </button>
                          )}
                          <button
                            className={`${isPartsURL
                              ? "add-brand-btn-parts"
                              : isAccessoriesURL
                                ? "add-brand-btn-acc"
                                : isGodownURL
                                  ? "add-brand-btn-godown"
                                  : "add-brand-btn"
                              }`}
                            onClick={handleAddStock}
                          >
                            <Plus size={16} />
                            Add Stock
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        className={`btn ${isPartsURL
                          ? "btn-outline-primary-parts batch-push-btn-parts"
                          : isAccessoriesURL
                            ? "btn-outline-primary-acc batch-push-btn-acc"
                            : isGodownURL
                              ? "batch-push-btn-godown"
                              : "btn-outline-primary batch-push-btn"
                          }`}
                        onClick={handlePushClick}
                      >
                        <PushIcon
                          fill={
                            isPartsURL
                              ? "var(--primary-color-parts)"
                              : isAccessoriesURL
                                ? "var(--primary-color-acc)"
                                : isGodownURL
                                  ? "#0CAE00"
                                  : "var(--primary-color-mobile)"
                          }
                        />
                        Push
                      </button>

                      <button
                        className={`${isPartsURL
                          ? "add-brand-btn-parts"
                          : isAccessoriesURL
                            ? "add-brand-btn-acc"
                            : isGodownURL
                              ? "add-brand-btn-godown"
                              : "add-brand-btn"
                          }`}
                        onClick={handleAddStock}
                      >
                        <Plus size={16} />
                        Add Stock
                      </button>
                    </>
                  )
                }
              </div>
            </div>
          </div>

          {/* Batch Summary Card */}
          <div className="batch-summary-card">
            <h3 className="batch-summary-title">Batch Summary</h3>
            <div className="batch-summary-grid">
              <div className="batch-summary-item">
                <div className="batch-summary-label">Batch No.</div>
                <div className="batch-summary-value">
                  {displayBatch.batch_number}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Supplier</div>
                <div className="batch-summary-value">{getSupplierName()}</div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Invoice No</div>
                <div className="batch-summary-value">
                  {displayBatch.invoice_number || "-"}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Description</div>
                <div className="batch-summary-value">
                  {displayBatch.description || "No description"}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Date</div>
                <div className="batch-summary-value">
                  {formatDate(displayBatch.created_at)}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Total Amount</div>
                <div
                  className={`batch-summary-value ${isPartsURL
                    ? "batch-amount-value-parts"
                    : isAccessoriesURL
                      ? "batch-amount-value-acc"
                      : isGodownURL
                        ? "batch-amount-value-godown"
                        : "batch-amount-value"
                    }`}
                >
                  €{formatPrice(totalAmountFromStocks)}/{displayBatch.amount}
                </div>
              </div>
              <div className="batch-summary-item">
                <div className="batch-summary-label">Total Quantity</div>
                <div className="batch-summary-value">
                  {displayBatch.total_qty || usedQuantity}/
                  {displayBatch.quantity}
                </div>
              </div>
            </div>
          </div>

          {/* Stock Items Card */}
          <div className="stock-items-card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 className="stock-items-title" style={{ margin: 0 }}>
                Stock Items
              </h3>

              {/* SearchBar */}
              <div
                className="d-flex align-items-center gap-3"
              // style={{ width: "548px" }}
              >
                {isGodownURL ? (
                  !displayBatch.is_published && (
                    <div className="page-actions">
                      <button
                        className={`btn batch-Tag-btn-godown`}
                        onClick={handleTagClick}
                      >
                        Price By Tag
                      </button>
                      <button
                        className={`${isPartsURL
                          ? "add-brand-btn-parts"
                          : isAccessoriesURL
                            ? "add-brand-btn-acc"
                            : isGodownURL
                              ? "add-brand-btn-godown"
                              : "add-brand-btn"
                          }`}
                        onClick={handlePrintBatch}
                      >
                        <PrintIcon fill={"#fff"} />
                        Print
                      </button>
                    </div>
                  )
                ) : (
                  <>
                    <div className="position-relative flex-grow-1">
                      <input
                        type="text"
                        className={`${isPartsURL
                          ? "brand-search-input-parts"
                          : isAccessoriesURL
                            ? "brand-search-input-acc"
                            : isGodownURL
                              ? "brand-search-input-godown"
                              : "brand-search-input"
                          } text-search`}
                        placeholder="Search stock items..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <Search
                        size={16}
                        className="position-absolute top-50 start-0 translate-middle-y ms-3"
                        style={{ color: "#8C959F" }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {(loadingBatchDetail && !searchLoading) || loadingStocks ? (
              <div className="stock-items-loading">
                Loading batch details...
              </div>
            ) : stocks.length === 0 ? (
              <div className="stock-items-empty">
                <div className="stock-items-empty-icon">
                  <img src={NoStock} alt="No Stock" />
                </div>
                <div className="stock-items-empty-text">
                  No stock items added to this batch yet
                </div>
              </div>
            ) : (
              <div
                className="stock-items-table-container"
                onScroll={handleScroll}
              >
                <table className="stock-items-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>PRODUCT</th>
                      <th>CATEGORY</th>
                      <th>BUYING PRICE</th>
                      <th>SELLING PRICE</th>
                      <th>QTY.</th>
                      <th>DATE</th>
                      <th>TOTAL AMOUNT</th>
                      <th style={{ textAlign: "center" }}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedStocks.map((stock, index) => {
                      // Handle the new API response structure from products_list
                      const buyingPrice = stock.buying_price || 0;
                      const sellingPrice = stock.selling_price || 0;
                      const quantity = stock.initial_quantity || 0;
                      const createdAt = stock.created_at;
                      const totalAmount =
                        stock.total_amount || buyingPrice * quantity;
                      const productName =
                        stock.product_name || "Unknown Product";
                      const categoryName = stock.category_name || "Mobile";
                      // const uniqueId = stock.unique_id || "";

                      // Calculate ID based on creation order
                      // sortedStocks has oldest first (index 0 = oldest = ID 1)
                      // reversedStocks has newest first (index 0 = newest = highest ID)
                      // Find the original position in sortedStocks to get the correct ID
                      const originalIndex = sortedStocks.findIndex(
                        (s) => s.id === stock.id
                      );
                      const displayId = originalIndex + 1;

                      const formatDate = (dateString) => {
                        if (!dateString) return "N/A";
                        return new Date(dateString).toLocaleDateString(
                          "en-US",
                          {
                            month: "numeric",
                            day: "numeric",
                            year: "numeric",
                          }
                        );
                      };

                      return (
                        <tr key={stock.id || index}>
                          <td>{displayId}</td>
                          <td className="product-name-cell">
                            {/* details of product name */}
                            <div
                              className="product-name-truncate"
                              title={productName}
                            >
                              {productName}
                            </div>
                            <div
                              className="product-details-truncate"
                              title={[
                                stock.product_detail?.ram,
                                stock.product_detail?.storage,
                                stock.product_detail?.variants
                                  ?.map((v) => Object.values(v).join(", "))
                                  .join(", "),
                                stock.product_detail?.suffix,
                                stock.product_detail?.used_mobile !==
                                  undefined &&
                                  stock.product_detail?.used_mobile !== null
                                  ? stock.product_detail.used_mobile === false
                                    ? "New"
                                    : "Used"
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" | ")}
                            >
                              {stock.product_detail?.ram &&
                                `${stock.product_detail.ram}`}
                              {stock.product_detail?.ram &&
                                stock.product_detail?.storage &&
                                " | "}
                              {stock.product_detail?.storage &&
                                `${stock.product_detail.storage}`}
                              {stock.product_detail?.variants &&
                                stock.product_detail.variants.length > 0 && (
                                  <>
                                    {(stock.product_detail?.ram ||
                                      stock.product_detail?.storage) &&
                                      " | "}
                                    {stock.product_detail.variants
                                      .map((variant) =>
                                        Object.values(variant).join(", ")
                                      )
                                      .join(", ")}
                                  </>
                                )}
                              {stock.product_detail?.suffix && (
                                <>
                                  {(stock.product_detail?.ram ||
                                    stock.product_detail?.storage ||
                                    (stock.product_detail?.variants &&
                                      stock.product_detail.variants.length >
                                      0)) &&
                                    " | "}
                                  {stock.product_detail.suffix}
                                </>
                              )}
                              {stock.product_detail?.used_mobile !==
                                undefined &&
                                stock.product_detail?.used_mobile !== null && (
                                  <>
                                    {(stock.product_detail?.ram ||
                                      stock.product_detail?.storage ||
                                      (stock.product_detail?.variants &&
                                        stock.product_detail.variants.length >
                                        0) ||
                                      stock.product_detail?.suffix) &&
                                      " | "}
                                    {stock.product_detail.used_mobile === false
                                      ? "New"
                                      : "Used"}
                                  </>
                                )}
                            </div>
                          </td>
                          <td className="category-cell">{categoryName}</td>
                          <td className="price-cell">
                            €{formatPrice(buyingPrice)}
                          </td>
                          <td className="price-cell">
                            €{formatPrice(sellingPrice)}
                          </td>
                          <td className="quantity-cell">{quantity}</td>
                          <td className="date-cell">{formatDate(createdAt)}</td>
                          <td className="total-amount-cell">
                            €{formatPrice(totalAmount)}
                          </td>
                          <td
                            className="action-cell"
                            style={{ borderBottom: "1px solid #f0f2f5" }}
                          >
                            {isGodownURL ?
                              <div className="action-buttons">
                                {!displayBatch.is_published && (
                                  <>
                                    <button
                                      className={` ${isPartsURL
                                        ? "action-btn-parts view"
                                        : isAccessoriesURL
                                          ? "action-btn-acc view"
                                          : isGodownURL
                                            ? "action-btn-godown view"
                                            : "action-btn view"
                                        }`}
                                      onClick={() => handleEditStock(stock)}
                                      title="Edit"
                                    >
                                      <EditIcon
                                        fill={
                                          isPartsURL
                                            ? "var(--primary-color-parts)"
                                            : isAccessoriesURL
                                              ? "var(--primary-color-acc)"
                                              : isGodownURL
                                                ? "var(--primary-color-godown)"
                                                : "var(--primary-color-mobile)"
                                        }
                                      />
                                    </button>
                                    <button
                                      className="action-btn delete"
                                      onClick={() => handleDeleteStockAPI(stock.id)}
                                      title="Delete"
                                    >
                                      <RedDeleteIcon />
                                    </button>
                                  </>
                                )}
                              </div>
                              : (
                                <div className="action-buttons">

                                  <>
                                    <button
                                      className={` ${isPartsURL
                                        ? "action-btn-parts view"
                                        : isAccessoriesURL
                                          ? "action-btn-acc view"
                                          : isGodownURL
                                            ? "action-btn-godown view"
                                            : "action-btn view"
                                        }`}
                                      onClick={() => handleEditStock(stock)}
                                      title="Edit"
                                    >
                                      <EditIcon
                                        fill={
                                          isPartsURL
                                            ? "var(--primary-color-parts)"
                                            : isAccessoriesURL
                                              ? "var(--primary-color-acc)"
                                              : isGodownURL
                                                ? "var(--primary-color-godown)"
                                                : "var(--primary-color-mobile)"
                                        }
                                      />
                                    </button>
                                    <button
                                      className="action-btn delete"
                                      onClick={() => handleDeleteStockAPI(stock.id)}
                                      title="Delete"
                                    >
                                      <RedDeleteIcon />
                                    </button>
                                  </>

                                </div>
                              )
                            }
                          </td>
                        </tr>
                      );
                    })}
                    {loadingStocks && (
                      <>
                        {[...Array(5)].map((_, i) => (
                          <tr key={`skeleton-${i}`} className="skeleton-row">
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                            <td>
                              <div className="skeleton-cell"></div>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* push confirmation modal */}
      {showPushConfirmation && isGodownURL && (
        <div className="push-confirmation-overlay">
          <div className="push-confirmation-modal">
            <div className="push-confirmation-icon">
              <img src={PushModalIcon} alt="Push Modal Icon" />
            </div>
            <h3 className="push-confirmation-title">Confirmation</h3>
            <p className="push-confirmation-message">
              Are you sure you want to publish the updated price tag?
              <br />
              This process cannot undo once confirmed.
            </p>
            <div className="push-confirmation-actions">
              <button className="push-cancel-btn" onClick={handlePushCancel}>
                No, Back to Batch
              </button>
              <button className="push-confirm-btn" onClick={handlePushConfirm}>
                Yes, Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showEditForm && (
        <BatchForm
          onSubmit={handleEditBatchSave}
          onCancel={() => setShowEditForm(false)}
          editingBatch={getEditableBatch()}
          suppliers={suppliers}
        />
      )}

      {showStockForm && (
        <StockForm
          onSubmit={handleStockFormSubmit}
          onCancel={() => {
            setShowStockForm(false);
            setEditingStock(null);
          }}
          editingStock={editingStock}
          products={products}
          suppliers={suppliers}
          allBatches={allBatches}
        />
      )}

      {/* add by tag modal */}
      {showAddByTagModal && isGodownURL && (
        <div className="add-by-tag-modal-overlay">
          <div className="add-by-tag-modal">
            <div className="add-by-tag-modal-header">
              <div>
                <h3 className="add-by-tag-modal-title">Add By Tag</h3>
                <p className="add-by-tag-modal-subtitle">
                  Fill in the details below to add tag into the batch.
                </p>
              </div>
            </div>

            <div className="add-by-tag-modal-body">
              <div className="batches-form-group tag-dropdown">
                <label className="batches-form-label">
                  Select by Tag <span className="required">*</span>
                </label>
                <div
                  className="tag-dropdown-input"
                  onClick={() => setTagDropdownOpen(!tagDropdownOpen)}
                >
                  <span className={tagFormData.selectedTag ? "selected-value" : "not-selected-value"}>
                    {tagFormData.selectedTag || "Select tag"}
                  </span>
                  <ChevronDown size={16} className="tag-dropdown-arrow" />
                </div>
                {tagDropdownOpen && (
                  <div className="tag-dropdown-menu">
                    <div className="tag-dropdown-search">
                      <Search size={16} className="tag-search-icon" />
                      <input
                        type="text"
                        className="tag-search-input"
                        placeholder="Search tags..."
                        value={tagSearchQuery}
                        onChange={(e) => setTagSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="tag-dropdown-list">
                      {filteredTags.length > 0 ? (
                        filteredTags.map((tag) => (
                          <div
                            key={tag.id}
                            className="tag-dropdown-item"
                            onClick={() => handleTagSelect(tag)}
                          >
                            {tag.identifier}
                          </div>
                        ))
                      ) : (
                        <div className="tag-dropdown-item no-results">
                          No tags found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="tag-quantity-row">
                <div className="batches-form-group" style={{ flex: 1 }}>
                  <label className="batches-form-label">
                    Quantity <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    className="batches-form-input"
                    placeholder="E.g. 4000"
                    value={tagFormData.quantity}
                    onChange={(e) =>
                      handleTagFormChange("quantity", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    min="0"
                  />
                  <div className="quantity-info">Maximum: 5690 units</div>
                </div>
                <div className="apply-to-all-wrapper">
                  <div className="apply-to-all-checkbox">
                    <input
                      type="checkbox"
                      id="applyToAll"
                      checked={tagFormData.applyToAll}
                      onChange={(e) =>
                        handleTagFormChange("applyToAll", e.target.checked)
                      }
                    />
                    <label htmlFor="applyToAll">
                      Apply to All
                      <span className="apply-to-all-info">
                        Entered quantity applied to all
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="batches-form-row">
                <div className="batches-form-group">
                  <label className="batches-form-label">
                    Buying Price <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    className="batches-form-input"
                    placeholder="E.g. €1,420.50"
                    value={tagFormData.buyingPrice}
                    onChange={(e) =>
                      handleTagFormChange("buyingPrice", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="batches-form-group">
                  <label className="batches-form-label">
                    Selling Price <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    className="batches-form-input"
                    placeholder="E.g. €1,420.50"
                    value={tagFormData.sellingPrice}
                    onChange={(e) =>
                      handleTagFormChange("sellingPrice", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* add by tag products table */}
              <div className="tag-products-table-container">
                <table className="tag-products-table">
                  <thead>
                    <tr>
                      <th>PRODUCTS</th>
                      <th>BUYING PRICE</th>
                      <th>SELLING PRICE</th>
                      <th>QUANTITY</th>
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tagProducts.length > 0 ? (
                      tagProducts.map((product, index) => (
                        <tr key={product.id}>
                          <td>
                            <div className="tag-product-name" title={product.name}>{product.name}</div>
                            <div
                              className="tag-product-details"
                              title={[
                                product.ram,
                                product.storage,
                                product.variants?.map((v) => Object.values(v).join(", ")).join(", "),
                                product.suffix,
                                product.used_mobile !== undefined && product.used_mobile !== null
                                  ? product.used_mobile === false ? "New" : "Used"
                                  : null,
                              ].filter(Boolean).join(" | ")}
                            >
                              {product.ram && `${product.ram}`}
                              {product.ram && product.storage && " | "}
                              {product.storage && `${product.storage}`}
                              {product.variants && product.variants.length > 0 && (
                                <>
                                  {(product.ram || product.storage) && " | "}
                                  {product.variants
                                    .map((variant) => Object.values(variant).join(", "))
                                    .join(", ")}
                                </>
                              )}
                              {product.suffix && (
                                <>
                                  {(product.ram ||
                                    product.storage ||
                                    (product.variants &&
                                      product.variants.length > 0)) &&
                                    " | "}
                                  {product.suffix}
                                </>
                              )}
                              {product.used_mobile !== undefined &&
                                product.used_mobile !== null && (
                                  <>
                                    {(product.ram ||
                                      product.storage ||
                                      (product.variants &&
                                        product.variants.length > 0) ||
                                      product.suffix) &&
                                      " | "}
                                    {product.used_mobile === false ? "New" : "Used"}
                                  </>
                                )}
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="tag-table-input"
                              value={productPrices[product.id]?.buyingPrice ?? (tagFormData.applyToAll ? tagFormData.buyingPrice : "")}
                              onChange={(e) => handleProductPriceChange(product.id, "buyingPrice", e.target.value)}
                              onWheel={(e) => e.target.blur()}
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="tag-table-input"
                              value={productPrices[product.id]?.sellingPrice ?? (tagFormData.applyToAll ? tagFormData.sellingPrice : "")}
                              onChange={(e) => handleProductPriceChange(product.id, "sellingPrice", e.target.value)}
                              onWheel={(e) => e.target.blur()}
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="tag-table-input"
                              value={productPrices[product.id]?.quantity ?? (tagFormData.applyToAll ? tagFormData.quantity : "")}
                              onChange={(e) => handleProductPriceChange(product.id, "quantity", e.target.value)}
                              onWheel={(e) => e.target.blur()}
                              placeholder="0"
                              min="0"
                            />
                          </td>
                          <td>
                            <div className="remove-product-btn-container">
                              <button
                                className="remove-product-btn"
                                onClick={() => handleRemoveTagProduct(index)}
                              >
                                ×
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                          {selectedTagId ? "No products found for this tag" : "Select a tag to view products"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="add-purchase-modal-footer">
              <button
                className="brand-cancel-btn"
                onClick={handleAddByTagCancel}
              >
                Cancel
              </button>
              <button
                className="brand-submit-btn-godown"
                onClick={handleAddByTagSubmit}
                disabled={submittingTag}
              >
                {submittingTag ? "Adding..." : "Add Tag"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* price by tag modal */}
      {showPriceByTagModal && isGodownURL && (
        <div className="add-by-tag-modal-overlay">
          <div className="add-by-tag-modal">
            <div className="add-by-tag-modal-header">
              <div>
                <h3 className="add-by-tag-modal-title">Price By Tag</h3>
                <p className="add-by-tag-modal-subtitle">
                  Fill in the details below to update price by tag into the
                  batch.
                </p>
              </div>
            </div>

            <div className="add-by-tag-modal-body">
              <div className="batches-form-group tag-dropdown">
                <label className="batches-form-label">
                  Select by Tag <span className="required">*</span>
                </label>
                <div
                  className="tag-dropdown-input"
                  onClick={() => setTagDropdownOpen(!tagDropdownOpen)}
                >
                  <span className={tagFormData.selectedTag ? "selected-value" : "not-selected-value"}>
                    {tagFormData.selectedTag || "Select tag"}
                  </span>
                  <ChevronDown size={16} className="tag-dropdown-arrow" />
                </div>
                {tagDropdownOpen && (
                  <div className="tag-dropdown-menu">
                    <div className="tag-dropdown-search">
                      <Search size={16} className="tag-search-icon" />
                      <input
                        type="text"
                        className="tag-search-input"
                        placeholder="Search tags..."
                        value={tagSearchQuery}
                        onChange={(e) => setTagSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="tag-dropdown-list">
                      {filteredTags.length > 0 ? (
                        filteredTags.map((tag) => (
                          <div
                            key={tag.id}
                            className="tag-dropdown-item"
                            onClick={() => handleTagSelect(tag)}
                          >
                            {tag.identifier}
                          </div>
                        ))
                      ) : (
                        <div className="tag-dropdown-item no-results">
                          No tags found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="tag-quantity-row">
                <div className="batches-form-group" style={{ flex: 1 }}>
                  <label className="batches-form-label">
                    Quantity <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    className="batches-form-input"
                    placeholder="E.g. 4000"
                    value={tagFormData.quantity}
                    onChange={(e) =>
                      handleTagFormChange("quantity", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    min="0"
                  />
                  <div className="quantity-info">Maximum: 5690 units</div>
                </div>
                <div className="apply-to-all-wrapper">
                  <div className="apply-to-all-checkbox">
                    <input
                      type="checkbox"
                      id="applyToAll"
                      checked={tagFormData.applyToAll}
                      onChange={(e) =>
                        handleTagFormChange("applyToAll", e.target.checked)
                      }
                    />
                    <label htmlFor="applyToAll">
                      Apply to All
                      <span className="apply-to-all-info">
                        Entered quantity applied to all
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="batches-form-row">
                <div className="batches-form-group">
                  <label className="batches-form-label">
                    Buying Price <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    className="batches-form-input"
                    placeholder="E.g. €1,420.50"
                    value={tagFormData.buyingPrice}
                    onChange={(e) =>
                      handleTagFormChange("buyingPrice", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="batches-form-group">
                  <label className="batches-form-label">
                    Selling Price <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    className="batches-form-input"
                    placeholder="E.g. €1,420.50"
                    value={tagFormData.sellingPrice}
                    onChange={(e) =>
                      handleTagFormChange("sellingPrice", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* price by tag products table */}
              <div className="tag-products-table-container">
                <table className="tag-products-table">
                  <thead>
                    <tr>
                      <th>PRODUCTS</th>
                      <th>BUYING PRICE</th>
                      <th>SELLING PRICE</th>
                      <th>QUANTITY</th>
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tagProducts.length > 0 ? (
                      tagProducts.map((product, index) => {
                        const productDetail = product.product_detail || product;
                        return (
                          <tr key={product.id}>
                            <td>
                              <div className="tag-product-name" title={product.product_name || productDetail.display_name || product.display_name || product.name}>
                                {product.product_name || productDetail.display_name || product.display_name || product.name}
                              </div>
                              <div
                                className="tag-product-details"
                                title={[
                                  productDetail.ram,
                                  productDetail.storage,
                                  productDetail.variants?.map((v) => Object.values(v).join(", ")).join(", "),
                                  productDetail.suffix,
                                  productDetail.used_mobile !== undefined && productDetail.used_mobile !== null
                                    ? productDetail.used_mobile === false ? "New" : "Used"
                                    : null,
                                ].filter(Boolean).join(" | ")}
                              >
                                {productDetail.ram && `${productDetail.ram}`}
                                {productDetail.ram && productDetail.storage && " | "}
                                {productDetail.storage && `${productDetail.storage}`}
                                {productDetail.variants && productDetail.variants.length > 0 && (
                                  <>
                                    {(productDetail.ram || productDetail.storage) && " | "}
                                    {productDetail.variants
                                      .map((variant) => Object.values(variant).join(", "))
                                      .join(", ")}
                                  </>
                                )}
                                {productDetail.suffix && (
                                  <>
                                    {(productDetail.ram ||
                                      productDetail.storage ||
                                      (productDetail.variants &&
                                        productDetail.variants.length > 0)) &&
                                      " | "}
                                    {productDetail.suffix}
                                  </>
                                )}
                                {productDetail.used_mobile !== undefined &&
                                  productDetail.used_mobile !== null && (
                                    <>
                                      {(productDetail.ram ||
                                        productDetail.storage ||
                                        (productDetail.variants &&
                                          productDetail.variants.length > 0) ||
                                        productDetail.suffix) &&
                                        " | "}
                                      {productDetail.used_mobile === false ? "New" : "Used"}
                                    </>
                                  )}
                              </div>
                            </td>
                            <td>
                              <input
                                type="number"
                                className="tag-table-input"
                                value={productPrices[product.id]?.buyingPrice ?? (tagFormData.applyToAll ? tagFormData.buyingPrice : "")}
                                onChange={(e) => handleProductPriceChange(product.id, "buyingPrice", e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="tag-table-input"
                                value={productPrices[product.id]?.sellingPrice ?? (tagFormData.applyToAll ? tagFormData.sellingPrice : "")}
                                onChange={(e) => handleProductPriceChange(product.id, "sellingPrice", e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="tag-table-input"
                                value={productPrices[product.id]?.quantity ?? (tagFormData.applyToAll ? tagFormData.quantity : "")}
                                onChange={(e) => handleProductPriceChange(product.id, "quantity", e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="0"
                                min="0"
                              />
                            </td>
                            <td>
                              <div className="remove-product-btn-container">
                                <button
                                  className="remove-product-btn"
                                  onClick={() => handleRemoveTagProduct(index)}
                                >
                                  ×
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                          {selectedTagId ? "No products found for this tag" : "Select a tag to view products"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="add-purchase-modal-footer">
              <button
                className="brand-cancel-btn"
                onClick={handleByTagCancel}
              >
                Cancel
              </button>
              <button
                className="brand-submit-btn-godown"
                onClick={handlePriceByTagSubmit}
                disabled={submittingTag}
              >
                {submittingTag ? "Updating..." : "Update Prices"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchDetailView;


